import React, {useCallback, useState} from 'react';
import { useApolloClient } from '@apollo/client';
import PropTypes, {arrayOf} from 'prop-types';
import { useTranslation } from 'react-i18next';
import ExpandedListTab from '../ExpandedListTab';
import SaveButton from '../buttons/SaveButton';
import TextField from '../fields/TextField';
import TextAreaField from '../fields/TextAreaField';
import RecipientsField from '../fields/RecipientsField';
import EmailTemplatePlaceholders from './EmailTemplatePlaceholders';
import './_styles_/EmailTemplateTab.css';
import {
    EMAIL_TEMPLATE_BODY_LENGTH,
    EMAIL_TEMPLATE_SUBJECT_LENGTH,
    EMAIL_TEMPLATE_NAME_LENGTH,
    EMAIL_TEMPLATE_RECIPIENTS_LENGTH,
    emailTemplateBodyValidator,
    emailTemplateNameExistsValidator,
    emailTemplateNameValidator,
    emailTemplateRecipientsValidator,
    emailTemplateSubjectValidator,
} from '../../validators/emailTemplates';
import useExtendedState from '../../hooks/useExtendedState';
import useMultiValidator from '../../hooks/useMultiValidator';
import useExtendedMutation from '../../hooks/useExtendedMutation';
import { ALL_EMAIL_TEMPLATES_QUERY_NAME, CREATE_EMAIL_TEMPLATE } from '../../graphql/emailTemplates';
import { compositeValidator, wafValidator } from '../../hooks/validationSupport';
import {emailTemplateType, productType} from '../../types';
import SelectField from "../fields/SelectField";
import {useDispatch} from "react-redux";
import CheckboxList from '../fields/CheckboxList';

EmailTemplateCreateTab.propTypes = {
    availableProducts: arrayOf(productType),
    hideHandler: PropTypes.func.isRequired,
    initialTemplate: emailTemplateType,
};

function EmailTemplateCreateTab(props) {
    const {
        availableProducts = [],
        hideHandler,
        initialTemplate = null
    } = props;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const apolloClient = useApolloClient();

    const nameState = useExtendedState(initialTemplate ? initialTemplate.name : '');
    const productState = useExtendedState(availableProducts.length ? availableProducts[0] : null);
    const phasesState = useExtendedState(initialTemplate?.phases || ['FINISHED']);
    const localeState = useExtendedState(initialTemplate?.locale || 'en');
    const recipientsState = useExtendedState(initialTemplate ? initialTemplate.recipients : '');
    const recipientsCcState = useExtendedState(initialTemplate ? initialTemplate.recipientsCc || '' : '');
    const recipientsBccState = useExtendedState(initialTemplate ? initialTemplate.recipientsBcc || '' : '');
    const subjectState = useExtendedState(initialTemplate?.subject || '');
    const bodyState = useExtendedState(initialTemplate ? initialTemplate.body : '');

    const { validators, isValid } = useMultiValidator(Boolean(initialTemplate), {
        name: compositeValidator(
            emailTemplateNameValidator(t),
            emailTemplateNameExistsValidator(t, dispatch, apolloClient, null)
        ),
        recipients: compositeValidator(emailTemplateRecipientsValidator(t), wafValidator(t)),
        recipientsCc: compositeValidator(emailTemplateRecipientsValidator(t, true), wafValidator(t)),
        recipientsBcc: compositeValidator(emailTemplateRecipientsValidator(t, true), wafValidator(t)),
        subject: compositeValidator(emailTemplateSubjectValidator(t), wafValidator(t)),
        body: compositeValidator(emailTemplateBodyValidator(t), (input) => wafValidator(t)(JSON.stringify(input).slice(1, -1))), // escape line feeds from input and trim apostrophes added by JSON.stringify
    }, {recipientsCc: true, recipientsBcc: true});

    const variables = {
        productId: productState.value ? productState.value['id'] : null,
        phases: phasesState.value,
        locale: localeState.value,
        name: nameState.value,
        recipients: recipientsState.value,
        recipientsCc: recipientsCcState.value,
        recipientsBcc: recipientsBccState.value,
        subject: subjectState.value,
        body: bodyState.value,
    };
    const extendedMutation = useExtendedMutation(CREATE_EMAIL_TEMPLATE, {
        variables,
        successCallback: hideHandler,
        successMessageOptions: { key: 'email-templates.notification.email-template-create-success', dataProperty: 'createEmailTemplate' },
        failureMessageOptions: { key: 'email-templates.notification.email-template-create-failure', params: { name: nameState.value } },
        refetchQueries: [ALL_EMAIL_TEMPLATES_QUERY_NAME],
    });
    const keyToProduct = useCallback((key) => availableProducts.find((p) => p.productId === key) || null, [availableProducts]);

    const [isDisabled, setIsDisabled] = useState(true);
    isValid()
        .then(() => setIsDisabled(false))
        .catch(() => setIsDisabled(true));

    return (
        <ExpandedListTab
            className="email-template-create-tab"
            title={t('email-templates.email-template-detail')}
        >
            <div className="rich-list-row-expanded--fields email-template-tab--fields">
                <div className="email-template-tab--left-col">
                    <TextField
                        label={t('email-templates.name')}
                        state={nameState}
                        className="email-template-create-tab--fields--name"
                        wrapperClassName="email-template-tab--stacked"
                        inputId="email-template-create-tab--fields--name"
                        validator={validators.name}
                        maxLength={EMAIL_TEMPLATE_NAME_LENGTH}
                        maximizeInputWidth={true}
                        invokeValidatorOnRender={initialTemplate != null}
                        isRequired
                    />
                    <SelectField
                        label={t('auctions.auction-setup.product')}
                        className="email-template-create-tab--fields--product"
                        inputId="email-template-create-tab--fields--product"
                        value={productState.value ? productState.value.productId : ''}
                        state={productState}
                        valueTransformer={keyToProduct}
                        options={availableProducts.map((p) => <option value={p.productId} key={p.productId}>{p.name} ({p.productId})</option>)}
                        isRequired
                    />
                    <CheckboxList
                        label={t('email-templates.phases')}
                        name="email-template-phases"
                        className="email-template-create-tab--fields--phases"
                        values={[
                            { id: 'OPEN', label: t('email-templates.phase-running') },
                            { id: 'REJECTED', label: t('email-templates.phase-rejected') },
                            { id: 'FINISHED', label: t('email-templates.phase-finished') },
                        ]}
                        state={phasesState}
                    />
                    <SelectField
                        label={t('email-templates.language')}
                        className="email-template-create-tab--fields--locale"
                        inputId="email-template-create-tab--fields--locale"
                        state={localeState}
                        isRequired
                        options={[
                            <option value="en" key="en">{t('email-templates.language-english')}</option>,
                            <option value="de" key="de">{t('email-templates.language-german')}</option>,
                        ]}
                    />
                    <TextField
                        label={t('email-templates.subject')}
                        state={subjectState}
                        className="email-template-create-tab--fields--subject wide"
                        wrapperClassName="email-template-tab--stacked"
                        inputId="email-template-create-tab--fields--subject"
                        validator={validators.subject}
                        maxLength={EMAIL_TEMPLATE_SUBJECT_LENGTH}
                        maximizeInputWidth={true}
                        isRequired
                    />
                    <TextAreaField
                        label={t('email-templates.body')}
                        state={bodyState}
                        className="email-template-create-tab--fields--body"
                        wrapperClassName="email-template-tab--body"
                        inputId="email-template-create-tab--fields--body"
                        validator={validators.body}
                        maxLength={EMAIL_TEMPLATE_BODY_LENGTH}
                        placeholder={t('email-templates.body-placeholder')}
                        textInstructions={<EmailTemplatePlaceholders />}
                        isRequired />
                </div>
                <div className={"email-template-tab--recipients-group"}>
                    <RecipientsField
                        label={t('email-templates.recipients')}
                        state={recipientsState}
                        wrapperClassName="email-template-tab--recipients"
                        inputId="email-template-create-tab--fields--recipients"
                        validator={validators.recipients}
                        maxLength={EMAIL_TEMPLATE_RECIPIENTS_LENGTH}
                        placeholder={t('email-templates.recipients-placeholder')}
                        addRecipientLabel={t('email-templates.add-recipient')}
                        removeRecipientLabel={t('email-templates.remove-recipient')}
                        isRequired
                    />
                    <RecipientsField
                        label={`${t('email-templates.recipients')} - Cc`}
                        state={recipientsCcState}
                        wrapperClassName="email-template-tab--recipients-cc"
                        inputId="email-template-create-tab--fields--recipients-cc"
                        validator={validators.recipientsCc}
                        maxLength={EMAIL_TEMPLATE_RECIPIENTS_LENGTH}
                        placeholder={t('email-templates.recipients-placeholder')}
                        addRecipientLabel={t('email-templates.add-recipient')}
                        removeRecipientLabel={t('email-templates.remove-recipient')}
                    />
                </div>
                <RecipientsField
                    label={`${t('email-templates.recipients')} - Bcc`}
                    state={recipientsBccState}
                    wrapperClassName="email-template-tab--recipients-bcc"
                    inputId="email-template-create-tab--fields--recipients-bcc"
                    validator={validators.recipientsBcc}
                    maxLength={EMAIL_TEMPLATE_RECIPIENTS_LENGTH}
                    placeholder={t('email-templates.recipients-placeholder')}
                    addRecipientLabel={t('email-templates.add-recipient')}
                    removeRecipientLabel={t('email-templates.remove-recipient')}
                />
            </div>
            <div className="rich-list-row-expanded--buttons">
                <SaveButton
                    extendedMutation={extendedMutation}
                    disabled={isDisabled}
                    confirmationMessage={t('email-templates.confirm-create-email-template', { name: nameState.value })}
                />
            </div>
        </ExpandedListTab>
    );
}

export default EmailTemplateCreateTab;
